import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import type { OnboardingTask } from '@feathr/blackbox';
import { EOnboardingTaskState } from '@feathr/blackbox';
import { Session } from '@feathr/blackbox';
import {
  EOnboardingProgressiveFeatures,
  useBlackboxVersion,
  useOnboardingStatus,
} from '@feathr/extender/hooks';
import { useAccount, useFlags, useLocalUrl, useRole } from '@feathr/extender/state';

import AccountsPage from '../AccountsPage';
import DashboardPage from '../DashboardPage';
import DashboardPageSkeleton from '../DashboardPage/DashboardPageSkeleton';
import DevelopmentQAPage from '../DevelopmentQAPage';
import NotificationsPage from '../NotificationsPage';
import OnboardingPage from '../OnboardingPage';
import MilestonePage from '../OnboardingPage/MilestonePage';
import VersionPage from '../VersionPage';
import DataRoutes from './DataRoutes';
import EventsRoutes from './EventsRoutes';
import MarketingRoutes from './MarketingRoutes';
import NotFound from './NotFound';
import OnboardingIncomplete from './OnboardingIncomplete';
import PermissionRequired from './PermissionRequired';
import RedirectTo404 from './RedirectTo404';
import RouteWithPermissions from './RouteWithPermissions';
import SettingsRoutes from './SettingsRoutes';

export function withAppcue(path: string, appcue?: string | null): string {
  if (!appcue) {
    return path;
  }
  const [base, search = ''] = path.split('?');
  const params = new URLSearchParams(search);
  params.set('appcue', appcue);
  return `${base}?${params.toString()}`;
}

function Routes(): JSX.Element {
  const localUrl = useLocalUrl();
  const account = useAccount();
  const { showOnboarding, enforceOnboarding } = useFlags();
  const { task: homeTask, isLoading: isHomeTaskLoading } = useOnboardingStatus(
    EOnboardingProgressiveFeatures.Home,
  );
  const { task: projectsTask } = useOnboardingStatus(EOnboardingProgressiveFeatures.Projects);
  const { task: communityTask } = useOnboardingStatus(EOnboardingProgressiveFeatures.Community);
  const { isOnProduction } = useBlackboxVersion();

  const { activePackage } = account;
  const { isAdmin, isSudoer, hasSegmentsSome, hasProjectsSome } = useRole();
  const hasProjectsOrSegments = hasProjectsSome || hasSegmentsSome;

  function isTaskNotCompleted(task: OnboardingTask | undefined): boolean {
    return (
      task?.get('state') === EOnboardingTaskState.InProgress ||
      task?.get('state') === EOnboardingTaskState.NotStarted
    );
  }

  const isOnboardingActive = showOnboarding && enforceOnboarding && account.isPlatformLite;

  const isHomeLocked =
    (isOnboardingActive && isTaskNotCompleted(homeTask)) || hasProjectsOrSegments;

  // Show loading state while onboarding status is being determined
  const shouldShowDashboardLoading = isOnboardingActive && isHomeTaskLoading;

  const isProjectsLocked =
    showOnboarding &&
    enforceOnboarding &&
    account.isPlatformLite &&
    isTaskNotCompleted(projectsTask);

  const isCommunityLocked =
    showOnboarding &&
    enforceOnboarding &&
    account.isPlatformLite &&
    isTaskNotCompleted(communityTask);
  const defaultOnboardingPath = '/:accountId/quickstart';
  const defaultRootPath = (function (): string {
    // On login, redirect Lite users to Quickstart based on their progress
    if (
      account.isPlatformLite &&
      showOnboarding &&
      enforceOnboarding &&
      homeTask?.get('state') !== EOnboardingTaskState.Completed
    ) {
      return defaultOnboardingPath;
    }

    if (!isHomeLocked) {
      return '/:accountId/dashboard';
    }

    return '/:accountId/marketing';
  })();

  if (!activePackage && !isSudoer) {
    return (
      <Switch>
        <Route path={'/:accountId/settings'}>
          <SettingsRoutes />
        </Route>
        <Redirect to={localUrl('/settings/billing/license')} />
        <RedirectTo404 />
      </Switch>
    );
  }

  const appcue = Session.getAppcueFlowId();

  return (
    <Switch>
      <Route path={'/404-not-found'}>
        <NotFound />
      </Route>
      <Route path={'/403-forbidden'}>
        <PermissionRequired />
      </Route>
      <Route path={'/version'}>
        <VersionPage />
      </Route>

      <Redirect exact={true} from={'/'} to={withAppcue(defaultRootPath, appcue)} />
      <Redirect exact={true} from={'/:accountId/'} to={withAppcue(defaultRootPath, appcue)} />

      <Route path={'/:accountId/404-not-found'}>
        <NotFound />
      </Route>
      <Route path={'/:accountId/403-forbidden'}>
        <PermissionRequired />
      </Route>
      {(isAdmin || isSudoer) && (
        <Route path={'/:accountId/accounts'}>
          <AccountsPage />
        </Route>
      )}

      <RouteWithPermissions
        condition={!shouldShowDashboardLoading && !isHomeLocked}
        fallback={
          shouldShowDashboardLoading ? (
            <DashboardPageSkeleton />
          ) : isOnboardingActive && isTaskNotCompleted(homeTask) ? (
            <OnboardingIncomplete />
          ) : undefined
        }
        path={'/:accountId/dashboard'}
      >
        <DashboardPage />
      </RouteWithPermissions>
      <RouteWithPermissions condition={!isCommunityLocked} path={'/:accountId/marketing'}>
        <MarketingRoutes />
      </RouteWithPermissions>
      <Route path={'/:accountId/data'}>
        <DataRoutes />
      </Route>
      <RouteWithPermissions condition={!isProjectsLocked} path={'/:accountId/projects'}>
        <EventsRoutes />
      </RouteWithPermissions>
      <Route path={'/:accountId/notifications'}>
        <NotificationsPage />
      </Route>
      <Route path={'/:accountId/settings'}>
        <SettingsRoutes />
      </Route>
      <Route path={'/:accountId/version'}>
        <VersionPage />
      </Route>
      {!isOnProduction && account.isTestAccount && (
        <Route path={'/:accountId/development-qa'}>
          <DevelopmentQAPage />
        </Route>
      )}

      {showOnboarding && (
        <>
          <Route exact={true} path={'/:accountId/quickstart'}>
            <OnboardingPage />
          </Route>
          <Route
            exact={true}
            path={
              '/:accountId/quickstart/:milestone(initial_account_setup|single_send_campaign|retargeting_campaign)'
            }
          >
            <MilestonePage />
          </Route>
        </>
      )}

      <RedirectTo404 />
    </Switch>
  );
}

export default observer(Routes);
